import { HEALTH_FINDING_TYPES, FINDING_LOCATIONS, SEVERITY_LEVELS } from "@/types/arboricultural-inspection";
import type { InspectionFormState, InspectionFormAction } from "../useInspectionForm";
import { useCtaCatalogs } from "@/hooks";
import TriState from "./TriState";

interface Props {
  state: InspectionFormState;
  dispatch: React.Dispatch<InspectionFormAction>;
}

const labelCls = "text-xs font-medium text-foreground-500 mb-1 block";
const selectCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";
const inputCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";

// Escala F1-F4 (MFI-AU). Os rotulos e cuidados vem do servidor (/cta/catalogs); este
// conjunto so existe para a tela nao ficar vazia se o catalogo nao carregar.
const F_FALLBACK = [
  { code: "F1", name: "Boa / vigorosa", status: "BOM", caution: "Descrever a observação e não somente marcar a classe." },
  { code: "F2", name: "Regular", status: "REGULAR", caution: "Documentar os sintomas e a sua extensão." },
  { code: "F3", name: "Ruim / declínio", status: "RUIM", caution: "Individualizar os achados e sugerir avaliação adicional quando necessário." },
  { code: "F4", name: "Morte iminente", status: "CRITICO", caution: "Exige fundamentação e rito técnico próprio. Não é ordem automática de supressão." },
  { code: "F4", name: "Morta", status: "MORTO", caution: "Exige fundamentação e rito técnico próprio. Não é ordem automática de supressão." },
];

const HEALTH_BOOLEANS: { field: string; label: string }[] = [
  { field: "pests", label: "Pragas" },
  { field: "diseases", label: "Doenças" },
  { field: "fungi", label: "Fungos" },
  { field: "termites", label: "Cupins" },
  { field: "cavities", label: "Cavidades" },
  { field: "rot", label: "Podridão" },
  { field: "dryBranches", label: "Galhos secos" },
];

const FINDING_TYPE_LABELS: Record<string, string> = {
  CAVITY: "Cavidade",
  WOOD_DECAY: "Decomposição",
  NECROSIS: "Necrose",
  CRACK: "Fissura",
  BREAKAGE: "Fratura",
  GIRDLING: "Anelamento",
  CANKER: "Cancro",
  WOUND: "Ferimento",
  TERMITES: "Cupins",
  BORERS: "Brocas",
  WOOD_DECAY_FUNGI: "Fungo xilófago",
  MISTLETOE: "Erva-de-passarinho",
  OTHER: "Outro",
};

const LOCATION_LABELS: Record<string, string> = {
  ROOT_COLLAR: "Colo",
  TRUNK: "Tronco",
  BIFURCATION: "Bifurcação",
  BRANCH: "Galho",
  CROWN: "Copa",
  ROOT: "Raiz",
  OTHER: "Outro",
};

const SEVERITY_LABELS: Record<string, string> = {
  LOW: "Baixa",
  MODERATE: "Moderada",
  HIGH: "Alta",
  NOT_ASSESSED: "Não avaliada",
};

export default function StepFitossanidade({ state, dispatch }: Props) {
  const ha = state.healthAssessment;
  const catalogs = useCtaCatalogs();

  // Uma opcao por estado guardado: F4 cobre "morta" e "morte iminente", que o banco distingue.
  const fOptions = catalogs
    ? catalogs.phytosanitary.flatMap((item) =>
        (item.stored_statuses ?? []).map((status) => ({
          code: item.code,
          name: item.stored_labels?.[status] ?? item.name,
          status,
          caution: item.caution,
        })),
      )
    : F_FALLBACK;
  const selectedF = fOptions.find((o) => o.status === ha.generalStatus) ?? null;

  return (
    <div className="space-y-5">
      {/* Classe fitossanitária F1-F4 */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-heart-pulse-line text-primary-500"></i>
          Condição fitossanitária (F1–F4)
        </h4>
        <p className="text-xs text-foreground-500 mt-1 mb-3">
          A classe descreve a condição observada na data. Ela não conclui risco nem manejo por si só.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2" role="radiogroup" aria-label="Condição fitossanitária">
          {fOptions.map((o) => {
            const selected = ha.generalStatus === o.status;
            return (
              <button
                key={o.status}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => dispatch({ type: "SET_HEALTH", field: "generalStatus", value: selected ? null : o.status })}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                  selected ? "border-primary-400 bg-primary-50" : "border-background-200/70 hover:bg-background-50"
                }`}
              >
                <span className="block text-sm font-bold text-foreground-800">{o.code}</span>
                <span className="block text-xs text-foreground-600 leading-snug">{o.name}</span>
              </button>
            );
          })}
        </div>
        {selectedF ? (
          <p className="mt-3 text-xs rounded-lg bg-background-50 border border-background-200/60 px-3 py-2 text-foreground-600">
            <strong className="text-foreground-800">{selectedF.code}:</strong> {selectedF.caution}
          </p>
        ) : (
          <p className="mt-3 text-xs text-foreground-400">Nenhuma classe escolhida. Ela é obrigatória para enviar a vistoria se houver indicadores marcados.</p>
        )}
      </div>

      {/* Indicadores em três estados */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800">Indicadores</h4>
        <p className="text-xs text-foreground-500 mt-1 mb-2">
          <strong>Não avaliado</strong> não é o mesmo que <strong>Ausente</strong>: só marque "Ausente" se você verificou.
        </p>
        <div className="divide-y divide-background-100">
          {HEALTH_BOOLEANS.map((b) => (
            <TriState
              key={b.field}
              label={b.label}
              value={(ha as Record<string, boolean | null | undefined>)[b.field]}
              onChange={(v) => dispatch({ type: "SET_HEALTH", field: b.field as never, value: v })}
            />
          ))}
        </div>
        <div className="mt-3">
          <label className={labelCls}>Observações fitossanitárias</label>
          <textarea
            rows={2}
            value={(ha.notes as string) ?? ""}
            onChange={(e) => dispatch({ type: "SET_HEALTH", field: "notes", value: e.target.value })}
            placeholder="Sintomas, extensão e fundamentação (obrigatória em F4)."
            className={`${inputCls} resize-none`}
          />
        </div>
      </div>

      {/* Health findings */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
            <i className="ri-bug-line text-warning-500"></i>
            Achados Fitossanitários
          </h4>
          <button
            type="button"
            onClick={() =>
              dispatch({
                type: "ADD_HEALTH_FINDING",
                finding: { findingType: "OTHER" },
              })
            }
            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <i className="ri-add-line"></i> Adicionar achado
          </button>
        </div>

        {state.healthFindings.length === 0 ? (
          <p className="text-xs text-foreground-400">Nenhum achado fitossanitário registrado.</p>
        ) : (
          <div className="space-y-3">
            {state.healthFindings.map((f, idx) => (
              <div key={idx} className="p-3 bg-background-50 rounded-lg border border-background-200/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-600">Achado #{idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => dispatch({ type: "REMOVE_HEALTH_FINDING", index: idx })}
                    className="text-xs text-danger-500 hover:text-danger-600"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Tipo</label>
                    <select
                      value={f.findingType}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_HEALTH_FINDING", index: idx, field: "findingType", value: e.target.value })
                      }
                      className={selectCls}
                    >
                      {HEALTH_FINDING_TYPES.map((t) => (
                        <option key={t} value={t}>{FINDING_TYPE_LABELS[t] ?? t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Localização</label>
                    <select
                      value={f.location ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_HEALTH_FINDING", index: idx, field: "location", value: e.target.value || null })
                      }
                      className={selectCls}
                    >
                      <option value="">—</option>
                      {FINDING_LOCATIONS.map((l) => (
                        <option key={l} value={l}>{LOCATION_LABELS[l] ?? l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Severidade</label>
                    <select
                      value={f.severity ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_HEALTH_FINDING", index: idx, field: "severity", value: e.target.value || null })
                      }
                      className={selectCls}
                    >
                      <option value="">—</option>
                      {SEVERITY_LEVELS.map((s) => (
                        <option key={s} value={s}>{SEVERITY_LABELS[s] ?? s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-2">
                  <input
                    type="text"
                    value={f.notes ?? ""}
                    onChange={(e) =>
                      dispatch({ type: "UPDATE_HEALTH_FINDING", index: idx, field: "notes", value: e.target.value || null })
                    }
                    placeholder="Notas..."
                    className={inputCls}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
